import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { addDays, addMonths, addWeeks, endOfWeek, format, startOfWeek } from 'date-fns';
import { enUS, he } from 'date-fns/locale';
import { bucketByDay, eventsFor } from './calendarEvents';
import MonthView from './MonthView';
import WeekView from './WeekView';
import AgendaView from './AgendaView';

export const MODES = ['month', 'week', 'agenda'];

/**
 * Props: orders (the server range result, already filtered), loading, notesSummary, availabilityDates,
 * mode, onModeChange, date, onDateChange, onOpenOrder(order), onToggleAvailability(dayKey, nowAvailable),
 * canEditAvailability, undatedCount (open SERVICE orders with no start — the range endpoint never
 * returns those, so the page counts them from the list), onShowUndated(), now (test seam).
 */
export default function OrdersCalendar({
  orders, loading = false, notesSummary = {}, availabilityDates = [], mode, onModeChange, date, onDateChange,
  onOpenOrder, onToggleAvailability, canEditAvailability = true, undatedCount = 0, onShowUndated, now = new Date(),
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'he' ? he : enUS;
  // The row itself mirrors under RTL (flex), but the chevrons do not: "previous" must point the
  // way the reader walks back, which is right-to-left in Hebrew.
  const rtl = useTheme().direction === 'rtl';
  const PrevIcon = rtl ? ChevronRight : ChevronLeft;
  const NextIcon = rtl ? ChevronLeft : ChevronRight;
  const events = useMemo(() => eventsFor(orders), [orders]);
  const buckets = useMemo(() => bucketByDay(events), [events]);

  const step = (dir) => {
    if (mode === 'month') onDateChange(addMonths(date, dir));
    else if (mode === 'week') onDateChange(addWeeks(date, dir));
    else onDateChange(addDays(date, dir * 7));
  };

  const heading = mode === 'month'
    ? format(date, 'LLLL yyyy', { locale })
    : `${format(startOfWeek(date, { locale }), 'd MMM', { locale })} – ${format(endOfWeek(date, { locale }), 'd MMM yyyy', { locale })}`;

  return (
    <Box data-testid="orders-calendar">
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" aria-label={t('orders.calendar.prev')} onClick={() => step(-1)}><PrevIcon /></IconButton>
          <IconButton size="small" aria-label={t('orders.calendar.next')} onClick={() => step(1)}><NextIcon /></IconButton>
          <ToggleButton size="small" value="today" selected={false} onClick={() => onDateChange(now)} sx={{ px: 1.5 }}>
            {t('orders.calendar.today')}
          </ToggleButton>
          {mode !== 'agenda' && <Typography variant="subtitle1" sx={{ ml: 1 }}>{heading}</Typography>}
        </Stack>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(e, v) => v && onModeChange(v)}>
          {MODES.map((m) => (
            <ToggleButton key={m} value={m} aria-label={t(`orders.calendar.${m}`)}>{t(`orders.calendar.${m}`)}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {mode === 'month' && (
            <MonthView
              date={date} buckets={buckets} notesSummary={notesSummary} availabilityDates={availabilityDates} locale={locale} now={now}
              onOpenOrder={onOpenOrder} onPickDay={(d) => { onDateChange(d); onModeChange('agenda'); }}
              onToggleAvailability={onToggleAvailability} canEditAvailability={canEditAvailability}
            />
          )}
          {mode === 'agenda' && (
            <AgendaView
              buckets={buckets} notesSummary={notesSummary} locale={locale} now={date} undatedCount={undatedCount}
              onOpenOrder={onOpenOrder} onShowUndated={onShowUndated}
            />
          )}
          {mode === 'week' && (
            <WeekView
              date={date} buckets={buckets} notesSummary={notesSummary} availabilityDates={availabilityDates} locale={locale} now={now}
              onOpenOrder={onOpenOrder}
            />
          )}
        </>
      )}
    </Box>
  );
}
